import { gql } from '@apollo/client';

export const FETCH_METRICS_QUERY = gql`
  query MetricsFetch($input: MetricsFetchInput!) {
    metricsFetch(input: $input) {
      metrics {
        metricKey
        entityType
        entityValue
        entityName
        metricType
        value
        valueType
        createdAt
        delta
      }
    }
  }
`;

export const TEAMS_GET_QUERY = gql`
  query TeamsGet($input: TeamsGetInput!) {
    teamsGet(input: $input) {
      teams {
        teamKey
        title
        description
        isIRA
        isLive
        createdAt
        companyUrl
        companyImageUrl
        verticalImageUrl
        portfolioCount
        followerCount
        isSubscribed
        isPlatformTeam
      }
    }
  }
`;

export const TEAM_PORTFOLIOS_QUERY = gql`
  query TeamPortfolios($input: TeamsGetInput!) {
    teamsGet(input: $input) {
      teams {
        teamKey
        portfolios {
          portfolioKey
          autoPilotMasterPortfolio {
            autoPilotMasterPortfolioKey
            title
            description
            profileImageUrl
            profileImageLargeUrl
            isLive
            archivedAt
          }
        }
      }
    }
  }
`;

export const TEAM_METRICS_QUERY = gql`
  query TeamMetrics($input: TeamsGetInput!, $metricsInput: MetricsInput) {
    teamsGet(input: $input) {
      teams {
        teamKey
        metrics(input: $metricsInput) {
          histogram {
            aum {
              metricKey
              entityType
              entityValue
              entityName
              metricType
              value
              valueType
              createdAt
              delta
            }
            subscriptions {
              metricKey
              entityType
              entityValue
              entityName
              metricType
              value
              valueType
              createdAt
              delta
            }
          }
        }
      }
    }
  }
`;

export const TEAM_PORTFOLIO_METRICS_QUERY = gql`
  query TeamPortfolioMetrics($input: TeamsGetInput!) {
    teamsGet(input: $input) {
      teams {
        portfolios(input: { limit: 50 }) {
          portfolioKey
          autoPilotMasterPortfolio {
            title
          }
          metrics(input: { metricsTimeRange: ONE_YEAR, metricsTimeMeasurementInterval: ONE_DAY }) {
            count {
              portfolioConversion {
                value
                delta
                createdAt
              }
            }
          }
        }
      }
    }
  }
`;

export interface Metric {
  metricKey: number;
  entityType: 'PORTFOLIO' | 'TEAM' | 'INSTITUTION';
  entityValue: number;
  entityName: string;
  metricType: 'PERCENTAGE' | 'COUNT' | 'INCREMENT';
  value: number;
  valueType: 'NEW_ACCOUNTS' | 'AUM';
  createdAt: string;
  delta: number | null;
}

export interface Team {
  teamKey: number;
  title: string | null;
  description: string;
  isIRA: boolean;
  isLive: boolean;
  createdAt: string;
  companyUrl: string | null;
  companyImageUrl: string | null;
  verticalImageUrl: string | null;
  portfolioCount: number | null;
  followerCount: number | null;
  isSubscribed: boolean | null;
  isPlatformTeam: boolean | null;
  portfolios?: Portfolio[];
}

export interface Portfolio {
  portfolioKey: number;
  autoPilotMasterPortfolio?: {
    autoPilotMasterPortfolioKey: number;
    title: string | null;
    description: string | null;
    profileImageUrl: string | null;
    profileImageLargeUrl: string | null;
    isLive: boolean;
    archivedAt: string | null;
  } | null;
}

export interface PortfolioMetrics {
  portfolioKey: number;
  autoPilotMasterPortfolio?: {
    title: string | null;
  } | null;
  metrics?: {
    count?: {
      portfolioConversion?: {
        value: number;
        delta: number | null;
        createdAt: string;
      }[];
    } | null;
  } | null;
}

export interface TeamPortfoliosInput {
  teamKeys: number[];
  limit?: number;
  offset?: number;
}

export interface TeamMetricsInput {
  teamKeys: number[];
  metricsInput?: MetricsInput;
}

export interface MetricsInput {
  metricsTimeRange?: 'FIVE_MINUTES' | 'TEN_MINUTES' | 'FIFTEEN_MINUTES' | 'THIRTY_MINUTES' | 'ONE_HOUR' | 'THREE_HOURS' | 'SIX_HOURS' | 'TWELVE_HOURS' | 'ONE_DAY' | 'ONE_WEEK' | 'ONE_MONTH' | 'SIXTY_DAYS' | 'NINETY_DAYS' | 'ONE_YEAR' | 'THREE_YEARS' | 'FIVE_YEARS' | 'MAX';
  metricsTimeMeasurementInterval?: 'ONE_MINUTE' | 'FIVE_MINUTES' | 'TEN_MINUTES' | 'FIFTEEN_MINUTES' | 'THIRTY_MINUTES' | 'ONE_HOUR' | 'THREE_HOURS' | 'SIX_HOURS' | 'TWELVE_HOURS' | 'ONE_DAY' | 'ONE_WEEK' | 'ONE_MONTH' | 'SIXTY_DAYS' | 'NINETY_DAYS' | 'ONE_YEAR' | 'THREE_YEARS' | 'FIVE_YEARS';
}

export interface GaugeMetric {
  aum: Metric[];
  accounts: Metric[];
  subscriptions: Metric[];
  followers: Metric[];
}

export interface HistogramMetric {
  aum: Metric[];
  accounts: Metric[];
  subscriptions: Metric[];
  followers: Metric[];
}

export interface CountMetric {
  portfolioConversion: Metric[];
}

export interface TeamMetrics {
  gauge: GaugeMetric;
  histogram: HistogramMetric;
  count: CountMetric;
}

export interface TeamsGetInput {
  teamKeys?: number[];
  limit: number;
  offset: number;
}

export interface TeamsGetResponse {
  teamsGet: {
    teams: Team[];
  };
}

export interface MetricsFetchInput {
  entityType: 'PORTFOLIO' | 'TEAM' | 'INSTITUTION';
  entityValue?: number;
  metricType: 'PERCENTAGE' | 'COUNT' | 'INCREMENT';
  metricValueType: 'NEW_ACCOUNTS' | 'AUM';
  metricsTimeRange: 'ONE_DAY' | 'ONE_WEEK' | 'ONE_MONTH' | 'SIXTY_DAYS' | 'NINETY_DAYS' | 'ONE_YEAR' | 'THREE_YEARS' | 'FIVE_YEARS' | 'MAX';
}

export interface MetricsFetchResponse {
  metricsFetch: {
    metrics: Metric[];
  };
} 